import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, apiError, eur, itDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Layout";
import { Empty, FormDialog, ListCard } from "@/components/Crud";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, ChevronRight, Receipt } from "lucide-react";

const STATUS_TONE = {
  programmato: "secondary",
  assegnato: "default",
  in_corso: "default",
  completato: "secondary",
  annullato: "outline",
};

export default function Interventi() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [plants, setPlants] = useState([]);
  const [techs, setTechs] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");

  const load = () =>
    api.get("/interventions", { params: filter ? { status: filter } : {} }).then(({ data }) => setRows(data));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  useEffect(() => {
    if (user?.role === "tecnico" || user?.role === "cliente") return;
    api.get("/customers").then(({ data }) => setCustomers(data));
    api.get("/plants").then(({ data }) => setPlants(data));
    api.get("/users", { params: { role: "tecnico" } }).then(({ data }) => setTechs(data)).catch(() => {});
  }, [user]);

  const fields = [
    {
      name: "customer_id", label: "Cliente", type: "select", required: true, full: true,
      options: customers.map((c) => ({ value: c.id, label: c.name })),
    },
    {
      name: "plant_id", label: "Impianto", type: "select", full: true,
      options: plants.map((p) => ({ value: p.id, label: `${p.name} — ${p.customer_name}` })),
    },
    {
      name: "type", label: "Tipo", type: "select",
      options: [
        { value: "manutenzione", label: "Manutenzione" },
        { value: "riparazione", label: "Riparazione" },
        { value: "installazione", label: "Installazione" },
      ],
    },
    {
      name: "priority", label: "Priorità", type: "select",
      options: ["bassa", "normale", "alta", "urgente"].map((p) => ({ value: p, label: p })),
    },
    {
      name: "technician_id", label: "Tecnico", type: "select",
      options: techs.map((t) => ({ value: t.id, label: t.name })),
    },
    { name: "scheduled_date", label: "Data", type: "date" },
    { name: "scheduled_time", label: "Ora", type: "time" },
    { name: "hourly_rate", label: "Tariffa oraria €", type: "number" },
    { name: "travel_cost", label: "Diritto di chiamata €", type: "number" },
    { name: "under_contract", label: "In contratto", type: "switch", hint: "Manodopera coperta dal contratto" },
    { name: "description", label: "Descrizione richiesta", type: "textarea", full: true },
  ];

  const save = async (values) => {
    try {
      await api.post("/interventions", values);
      await load();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  const doInvoice = async (i) => {
    try {
      await api.post(`/interventions/${i.id}/invoice`);
      await load();
    } catch (e) {
      /* apri il dettaglio per il messaggio d'errore completo, se serve */
    }
  };

  return (
    <div data-testid="interventi-page">
      <PageHeader title="Interventi tecnici" subtitle={`${rows.length} interventi`}>
        <div className="flex gap-2 flex-wrap">
          {["", "programmato", "assegnato", "in_corso", "completato"].map((s) => (
            <Button
              key={s || "all"}
              variant={filter === s ? "default" : "outline"}
              size="sm"
              className="rounded-sm capitalize"
              onClick={() => setFilter(s)}
              data-testid={`filter-${s || "tutti"}`}
            >
              {s ? s.replace("_", " ") : "tutti"}
            </Button>
          ))}
        </div>
        {(user?.role === "azienda" || user?.role === "agente") && (
          <Button className="h-11 rounded-sm" onClick={() => setOpen(true)} data-testid="new-intervention-btn">
            <Plus className="h-4 w-4 mr-2" /> Nuovo intervento
          </Button>
        )}
      </PageHeader>

      <ListCard testid="interventi-list">
        {rows.map((i) => (
          <div key={i.id} className="px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs text-muted-foreground">{i.code}</span>
                <p className="font-semibold truncate">{i.customer_name}</p>
                <Badge className="rounded-sm capitalize" variant={STATUS_TONE[i.status] || "secondary"}>
                  {i.status.replace("_", " ")}
                </Badge>
                <Badge variant="outline" className="rounded-sm capitalize">{i.type}</Badge>
                {i.under_contract && <Badge variant="secondary" className="rounded-sm">in contratto</Badge>}
                {i.invoice_status && (
                  <Badge variant={i.invoice_status === "incassato" ? "secondary" : "outline"}
                         className="rounded-sm capitalize">
                    {i.invoice_status.replace("_", " ")}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {itDate(i.scheduled_date)} {i.scheduled_time || ""} · {i.plant_name || "—"} ·{" "}
                {i.technician_name || "non assegnato"} · {eur(i.totals?.total)}
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              {user?.role === "azienda" && i.invoice_status === "da_fatturare" && (
                <Button variant="outline" size="sm" className="rounded-sm" onClick={() => doInvoice(i)}
                        data-testid={`invoice-intervention-${i.id}`}>
                  <Receipt className="h-3.5 w-3.5 mr-2" /> Fattura
                </Button>
              )}
              <Button asChild size="sm" className="rounded-sm">
                <Link to={`/interventi/${i.id}`} data-testid={`open-intervention-${i.id}`}>
                  Apri <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Link>
              </Button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <Empty text="Nessun intervento." />}
      </ListCard>

      <FormDialog open={open} onOpenChange={setOpen} title="Nuovo intervento" fields={fields}
                  initial={{ type: "manutenzione", priority: "normale", hourly_rate: 45 }}
                  onSubmit={save} testid="intervention-dialog" />
    </div>
  );
}
